/**
 * Manage Compliance submit for the Log Review main dashboard.
 *
 *   POST /logReviewer/api/v1/sirar/manageCompliance
 *
 * Raises a justification against the selected non-compliant CR(s).
 * `justifiedBy` is the signed-in username (the backend stamps from the
 * payload, not the auth header) and `justifiedOn` is local submit time
 * without a timezone.
 */

import { LOG_REVIEW_URL } from "../constants/index";
import { useAuthStore } from "../stores/authStore";
import {
  logReviewPostForm,
  toWriteResult,
  type LogReviewWriteResponse,
  type LogReviewWriteResult,
} from "./logReviewClient";

const MANAGE_PATH = `${LOG_REVIEW_URL.SIRAR}/manageCompliance`;

export interface ManageComplianceRequest {
  /** Numeric CR record ids for the selected rows. */
  id: number[];
  description: string;
  justifiedBy: string;
  /** Local ISO without timezone — `YYYY-MM-DDTHH:mm:ss`. */
  justifiedOn: string;
}

export type ManageComplianceResult = LogReviewWriteResult;

const nowIsoNoTz = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

export const buildManageCompliancePayload = (
  rowIds: string[],
  description: string,
  justifiedBy: string,
): ManageComplianceRequest => ({
  id: rowIds.map((rid) => Number(rid)).filter((n) => Number.isFinite(n)),
  description,
  justifiedBy,
  justifiedOn: nowIsoNoTz(),
});

export const submitManageCompliance = async (
  rowIds: string[],
  description: string,
  attachments: File[] = [],
  signal?: AbortSignal,
): Promise<ManageComplianceResult> => {
  const justifiedBy = useAuthStore.getState().user?.username ?? "";
  const payload = buildManageCompliancePayload(rowIds, description, justifiedBy);

  // The endpoint always expects multipart/form-data with a `sirarRequestVO`
  // JSON part — the file part is optional. Sending a raw JSON body when there
  // are no attachments makes the backend 500, so always build the form.
  const form = new FormData();
  form.append(
    "sirarRequestVO",
    new Blob([JSON.stringify(payload)], { type: "application/json" }),
  );
  attachments.forEach((file) => form.append("file", file));

  const raw = await logReviewPostForm<LogReviewWriteResponse>(MANAGE_PATH, form, signal);
  return toWriteResult(raw ?? {});
};
